import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PurchaseOrderService, { PurchaseOrder, PurchaseOrderItem } from '../../services/purchase-order.service';
import SupplierService from '../../services/supplier.service';
import ProductService from '../../services/product.service';
import { NumberTextField } from '../../components/common';

interface LineItem {
  tempId: string;
  id?: number;
  product: any;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
  total: number;
}

const toDateOnly = (v: string | undefined): string => {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  return v;
};

const PurchaseOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<PurchaseOrder>({
    orderNumber: `PO-${Date.now()}`,
    orderDate: new Date().toISOString().split('T')[0],
    expectedDelivery: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    status: 'draft',
    notes: '',
  });

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    if (isEdit && id) {
      fetchPurchaseOrder(parseInt(id));
    }
  }, [id, isEdit]);

  // Refetch suppliers when window regains focus (e.g. after creating one in new tab)
  useEffect(() => {
    const onFocus = () => fetchSuppliers();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const response = await SupplierService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setSuppliers(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchPurchaseOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.getOne(orderId);
      const orderData = response.data?.data;

      if (orderData) {
        const attrs = orderData.attributes;
        setFormData({
          orderNumber: attrs.orderNumber,
          supplier: attrs.supplier?.data?.id ?? undefined,
          orderDate: toDateOnly(attrs.orderDate),
          expectedDelivery: toDateOnly(attrs.expectedDelivery) || '',
          actualDelivery: toDateOnly(attrs.actualDelivery) || '',
          subtotal: attrs.subtotal || 0,
          tax: attrs.tax || 0,
          discount: attrs.discount || 0,
          total: attrs.total || 0,
          status: attrs.status || 'draft',
          notes: attrs.notes || '',
        });

        if (attrs.supplier?.data) setSelectedSupplier(attrs.supplier.data);

        const itemsResponse = await PurchaseOrderService.getItems(orderId);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => {
          const qty = item.attributes?.quantity || 1;
          const price = item.attributes?.unitPrice || 0;
          const disc = item.attributes?.discount || 0;
          const taxAmount = item.attributes?.tax || 0;
          const lineSub = qty * price - disc;
          const taxPercent = lineSub > 0 ? (taxAmount / lineSub) * 100 : 5;
          return {
            tempId: `item-${item.id}`,
            id: item.id,
            product: item.attributes?.product?.data || null,
            description: item.attributes?.description || (item.attributes?.product?.data?.attributes?.name || ''),
            quantity: qty,
            unitPrice: price,
            discount: disc,
            tax: taxPercent,
            total: item.attributes?.total || 0,
          };
        });
        
        setLineItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      setError('Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      tempId: `temp-${Date.now()}`,
      product: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 5,
      total: 0,
    }]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          
          if (field === 'product' && value) {
            updated.unitPrice = Number(value.attributes?.price) || 0;
            updated.description = value.attributes?.name || '';
          }
          if (field === 'product' && !value) {
            updated.description = updated.description || '';
          }
          
          if (field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'tax') {
            updated[field] = Number(value) || 0;
          }
          
          const qty = Number(updated.quantity) || 0;
          const price = Number(updated.unitPrice) || 0;
          const discount = Number(updated.discount) || 0;
          const tax = Number(updated.tax) || 0;
          
          const subtotal = qty * price;
          const taxAmount = (subtotal - discount) * (tax / 100);
          updated.total = subtotal - discount + taxAmount;
          
          return updated;
        }
        return item;
      })
    );
  };

  useEffect(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitPrice)), 0);
    const totalDiscount = lineItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const totalTax = lineItems.reduce((sum, item) => {
      const lineSub = Number(item.quantity) * Number(item.unitPrice) - (Number(item.discount) || 0);
      return sum + lineSub * ((Number(item.tax) || 0) / 100);
    }, 0);
    const total = subtotal - totalDiscount + totalTax;

    setFormData((prev) => ({
      ...prev,
      subtotal: Number(subtotal) || 0,
      discount: Number(totalDiscount) || 0,
      tax: Number(totalTax) || 0,
      total: Number(total) || 0,
    }));
  }, [lineItems]);

  const validateForm = (): boolean => {
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    if (lineItems.some(item => !item.product && !item.description?.trim())) {
      setError('Each line item must have a product selected or a description entered');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let orderId: number;

      const orderPayload: PurchaseOrder = {
        ...formData,
        orderDate: toDateOnly(formData.orderDate) || formData.orderDate,
        expectedDelivery: formData.expectedDelivery ? toDateOnly(formData.expectedDelivery) : undefined,
        actualDelivery: formData.actualDelivery ? toDateOnly(formData.actualDelivery) : undefined,
      };
      if (!orderPayload.supplier) delete (orderPayload as unknown as Record<string, unknown>).supplier;
      if (isEdit && id) {
        await PurchaseOrderService.update(parseInt(id), orderPayload);
        orderId = parseInt(id);
        setSuccess('Purchase Order updated!');
      } else {
        const response = await PurchaseOrderService.create(orderPayload);
        orderId = response.data?.data?.id;
        setSuccess('Purchase Order created!');
      }

      if (orderId) {
        if (isEdit) {
          const oldItemsResponse = await PurchaseOrderService.getItems(orderId);
          const oldItems = oldItemsResponse.data?.data || [];
          await Promise.all(oldItems.map((item: any) => PurchaseOrderService.deleteItem(item.id)));
        }

        await Promise.all(
          lineItems.map((item) => {
            const lineSub = Number(item.quantity) * Number(item.unitPrice) - (Number(item.discount) || 0);
            const taxAmount = lineSub * ((Number(item.tax) || 0) / 100);
            return PurchaseOrderService.createItem({
              purchaseOrder: orderId,
              ...(item.product?.id ? { product: item.product.id } : {}),
              description: item.description || (item.product?.attributes?.name || ''),
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount,
              tax: taxAmount,
              total: item.total,
            });
          })
        );
      }

      setTimeout(() => navigate('/purchase-orders'), 1500);
    } catch (error: any) {
      console.error('Error saving purchase order:', error);
      setError(error.response?.data?.error?.message || 'Failed to save purchase order');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Order Details</Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Order Number"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="partiallyReceived">Partially Received</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                <Autocomplete
                  sx={{ minWidth: 280, flex: 1 }}
                  value={selectedSupplier}
                  onChange={(e, v) => {
                    setSelectedSupplier(v);
                    setFormData({ ...formData, supplier: v?.id ?? undefined });
                  }}
                  options={suppliers}
                  loading={suppliersLoading}
                  getOptionLabel={(o) => o.attributes?.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier (Optional)"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {suppliersLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => window.open('/suppliers/create', '_blank')}
                  sx={{ minWidth: 200, height: 56 }}
                >
                  Create new Supplier
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Order Date"
                  name="orderDate"
                  type="date"
                  value={formData.orderDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Expected Delivery"
                  name="expectedDelivery"
                  type="date"
                  value={formData.expectedDelivery}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Line Items</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addLineItem} size="small">
                Add Item
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Product / Description</TableCell>
                    <TableCell width="10%">Qty</TableCell>
                    <TableCell width="15%">Unit Price</TableCell>
                    <TableCell width="12%">Discount</TableCell>
                    <TableCell width="12%">Tax %</TableCell>
                    <TableCell width="15%">Total</TableCell>
                    <TableCell width="6%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No items added. Click "Add Item" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.tempId}>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            value={item.product ? item.product : (item.description || '')}
                            onInputChange={(_, inputValue) => {
                              setLineItems((prev) =>
                                prev.map((it) =>
                                  it.tempId === item.tempId
                                    ? { ...it, description: inputValue, product: null }
                                    : it
                                )
                              );
                            }}
                            onChange={(e, v) => {
                              if (v && typeof v === 'object' && v.attributes) {
                                updateLineItem(item.tempId, 'product', v);
                              }
                            }}
                            options={products}
                            loading={productsLoading}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : `${o?.attributes?.name || ''} (${o?.attributes?.sku || ''})`)}
                            renderInput={(params) => <TextField {...params} size="small" placeholder="Select product or type description" />}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.quantity}
                            onValueChange={(v) => updateLineItem(item.tempId, 'quantity', v)}
                            onChange={(e) => updateLineItem(item.tempId, 'quantity', e.target.value)}
                            inputProps={{ min: 1 }}
                            allowDecimal={false}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.unitPrice}
                            onValueChange={(v) => updateLineItem(item.tempId, 'unitPrice', v)}
                            onChange={(e) => updateLineItem(item.tempId, 'unitPrice', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">OMR</InputAdornment> }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.discount}
                            onValueChange={(v) => updateLineItem(item.tempId, 'discount', v)}
                            onChange={(e) => updateLineItem(item.tempId, 'discount', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">OMR</InputAdornment> }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.tax}
                            onValueChange={(v) => updateLineItem(item.tempId, 'tax', v)}
                            onChange={(e) => updateLineItem(item.tempId, 'tax', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            OMR {item.total.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeLineItem(item.tempId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="h6">OMR {formData.subtotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" color="error">Discount:</Typography>
                  <Typography variant="h6" color="error">- OMR {formData.discount.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Tax:</Typography>
                  <Typography variant="h6">OMR {formData.tax.toFixed(2)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h5">Total:</Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    OMR {formData.total.toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/purchase-orders')} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Order' : 'Create Order'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default PurchaseOrderForm;

